// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { TypographyProps } from "@insite/mobius/Typography";
import TrainingCalenderListFiltersAccordionSection from "@werner/App/Components/TrainingCalender/TrainingCalenderListAccordionSection";
import { xGoogleAnalyticsState } from "@werner/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@werner/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import setEventFilters from "@werner/App/Handlers/TrainingCalender/SetEventFilter";
import { ModifiedTrainingFacetValue } from "@werner/App/Services/EventService";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import { color } from "@werner/XDL/Utilities";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        facets: state.trainingCalender.facets,
        selectedFilters: state.trainingCalender.selectedFilters,
        xGoogleAnalytics: (state as unknown as xGoogleAnalyticsState).xGoogleAnalytics,
        isAuthenticated: xIsAuthenticated(state),
    };
};

const mapDispatchToProps = {
    setEventFilters,
};

interface OwnProps extends WidgetProps {}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const TrainingCalenderListFilter: React.FC<Props> = ({
    facets,
    setEventFilters,
    selectedFilters,
    xGoogleAnalytics,
    isAuthenticated,
}) => {
    const onChangeFacet = (facet: ModifiedTrainingFacetValue) => {
        const exist = selectedFilters[facet.parent] === facet.Name;
        if (!exist) {
            setEventFilters({ name: facet.Name, parent: facet.parent });
            sendTracking(facet);
        } else {
            setEventFilters({ name: "", parent: facet.parent });
        }
    };

    // Xng-Change
    // WES-1782
    const sendTracking = (facet: ModifiedTrainingFacetValue) => {
        const dataLayer = getDataLayer();

        if (!dataLayer || !xGoogleAnalytics.trainingFilterEvent) {
            return;
        }

        const googleAnalyticsData = {
            event: xGoogleAnalytics.trainingFilterEventName,
            click_text: facet.Description,
            affiliation: xGoogleAnalytics.affiliation,
            authentication_state: isAuthenticated ? "authenticated" : "unauthenticated",
        };

        dataLayer.push(googleAnalyticsData);
    };

    return (
        <StyledWrapper>
            {facets.map(a =>
                a.Values ? (
                    <TrainingCalenderListFiltersAccordionSection
                        key={a.Id}
                        title={a.Description}
                        facetValues={a.Values.map<ModifiedTrainingFacetValue>(av => ({
                            ...av,
                            parent: a.Name,
                        }))}
                        onChangeFacet={onChangeFacet}
                        showMoreLimit={3}
                        expandByDefault={true}
                        selectedFilters={selectedFilters}
                    />
                ) : null,
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(TrainingCalenderListFilter),
    definition: {
        group: "Xngage Custom" as any,
    },
};

export default widgetModule;
